import { useCallback, useEffect, useRef } from "react";
import { ActionKind } from "./form-view-reducer";
import * as pdfjsLib from "pdfjs-dist";
import * as PDFWorker from "pdfjs-dist/legacy/build/pdf.worker.min.mjs";

export function usePdfEffects(state, dispatch, canvasRefs) {

    pdfjsLib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(
        new Blob([PDFWorker], { type: "application/javascript" })
    );

    const convertPageToImage = async (pageNumber: number) => {

        if (!state.pdf)
            return;

        const page = await state.pdf.getPage(pageNumber + 1);
        const canvas = canvasRefs.current[pageNumber] as HTMLCanvasElement;

        if (!canvas)
            return;

        const scale = 1.5;
        const viewport = page.getViewport({ scale: scale, });
        // Support HiDPI-screens.
        const outputScale = window.devicePixelRatio || 1;

        const context = canvas.getContext('2d');

        if (!context)
            return;

        context.imageSmoothingEnabled = false;

        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = viewport.height;
        canvas.style.height = viewport.width;

        const transform = outputScale !== 1
            ? [outputScale, 0, 0, outputScale, 0, 0]
            : null;

        const renderContext = {
            canvasContext: context,
            transform: transform,
            viewport: viewport
        };

        await page.render(renderContext).promise;

        return canvas.toDataURL('image/png');
    }

    const base64StringtoBlob = useCallback((b64Data: string, pdf: any): Blob => {

        if (!b64Data || b64Data.length === 0)
            return;

        const contentType = 'application/pdf';
        const sliceSize = 512;

        const split = b64Data.split(',');
        const byteCharacters = atob(split[1]);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: contentType });
    }, []);


    // Track if PDF conversion has run
    const pdfInitialized = useRef(false);

    // PDF: Convert PDF to images when URI or PDF changes
    useEffect(() => {

        if (!state.uri || pdfInitialized.current)
            return;

        const convertPdfToImages = async () => {

            if (!state.uri)
                return;

            const pdf = await pdfjsLib.getDocument({ url: state.uri }).promise;

            if (!pdf)
                return;

            dispatch({ type: ActionKind.SET_PDF, payload: pdf });

            const newPages = [];

            for (let index = 0; index < pdf.numPages; index++) {
                newPages.push(index);
            }

            dispatch({ type: ActionKind.SET_PAGE_PLACEHOLDERS, payload: newPages });

        }

        convertPdfToImages();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.uri]);


    useEffect(() => {

        if (!state.pagePlacesHolders || !state.pdf)
            return;

        const getPages = async () => {

            const newImages = [];

            for (let index = 0; index < state.pagePlacesHolders.length; index++) {
                const image = await convertPageToImage(index);

                newImages.push(image);

            }

            dispatch({ type: ActionKind.SET_IMAGES, payload: newImages });
            dispatch({ type: ActionKind.SET_LOADING, payload: false });

        }

        getPages();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.pagePlacesHolders]);

    // PDF: When data.json.pdf changes, set URI
    useEffect(() => {

        if (!state.data?.json?.pdf)
            return;

        const blob = base64StringtoBlob(state.data.json.pdf, state.pdf);

        dispatch({ type: ActionKind.SET_URI, payload: URL.createObjectURL(blob) });


        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.data?.json?.pdf]);
}
